class ClassClopePredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.abortController = null;
    this.timer = null;
    this.fallbackTerm = '';
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search]');

    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => this.handleInput());
    this.input.addEventListener('focus', () => this.handleFocus());
    this.input.form?.addEventListener('submit', () => {
      const enteredTerm = this.input.value.trim();
      if (!enteredTerm) return;

      const resolvedTerm = this.fallbackTerm || this.humanize(enteredTerm);
      if (this.normalize(resolvedTerm) !== this.normalize(enteredTerm)) this.input.value = resolvedTerm;
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 160);
    });
  }

  handleInput() {
    window.clearTimeout(this.timer);
    this.fallbackTerm = '';
    const term = this.input.value.trim();

    if (term.length < 2) {
      this.close(true);
      return;
    }

    this.timer = window.setTimeout(() => this.search(term), 180);
  }

  handleFocus() {
    if (this.input.value.trim().length >= 2 && this.results.innerHTML.trim()) this.open();
  }

  async search(term) {
    const cacheKey = this.normalize(term);

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      this.fallbackTerm = cached.resolvedTerm === term ? '' : cached.resolvedTerm;
      this.render(cached.markup, term);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setAttribute('loading', '');

    try {
      let markup = await this.fetchMarkup(term);
      let resolvedTerm = term;

      if (!this.hasProducts(markup)) {
        const correctedTerm = this.humanize(term);
        if (this.normalize(correctedTerm) !== this.normalize(term)) {
          const correctedMarkup = await this.fetchMarkup(correctedTerm);
          if (this.hasProducts(correctedMarkup)) {
            markup = this.addCorrectionNotice(correctedMarkup, correctedTerm);
            resolvedTerm = correctedTerm;
          }
        }
      }

      this.cache.set(cacheKey, { markup, resolvedTerm });
      this.fallbackTerm = resolvedTerm === term ? '' : resolvedTerm;
      this.render(markup, term);
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.removeAttribute('loading');
    }
  }

  async fetchMarkup(term) {
    const baseUrl = window.routes?.predictive_search_url || '/search/suggest';
    const params = new URLSearchParams({
      q: term,
      section_id: 'predictive-search',
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      'resources[options][unavailable_products]': 'last',
    });
    const response = await fetch(`${baseUrl}?${params.toString()}`, {
      signal: this.abortController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Predictive search: ${response.status}`);

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const section = documentFragment.querySelector('#shopify-section-predictive-search');
    return section ? section.innerHTML : '';
  }

  hasProducts(markup) {
    if (!markup) return false;
    const fragment = new DOMParser().parseFromString(markup, 'text/html');
    return Boolean(fragment.querySelector('#predictive-search-results-products-list .predictive-search__list-item'));
  }

  normalize(value) {
    return value
      .toLocaleLowerCase('fr')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’'_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  humanize(term) {
    const directCorrections = {
      arome: 'arôme', aromes: 'arômes', cartuche: 'cartouche', cartuches: 'cartouches',
      clearomizer: 'clearomiseur', clearomiseure: 'clearomiseur', eliquide: 'e-liquide',
      eliquides: 'e-liquides', liqude: 'liquide', liquides: 'liquides', menth: 'menthe',
      nicotin: 'nicotine', citon: 'citron', cirton: 'citron', resistence: 'résistance', resistences: 'résistances',
      resistance: 'résistance', resistances: 'résistances', vaporeso: 'vaporesso',
      voopo: 'voopoo', geekvap: 'geekvape', innokin: 'innokin', aspiree: 'aspire',
    };
    const vocabulary = [
      'arôme', 'arômes', 'cartouche', 'cartouches', 'clearomiseur', 'e-liquide', 'e-liquides',
      'fruité', 'frais', 'classic', 'menthe', 'nicotine', 'pod', 'pods', 'puff', 'résistance',
      'résistances', 'vaporesso', 'voopoo', 'geekvape', 'innokin', 'aspire', 'smok', 'lost vape',
      'citron', 'fraise', 'framboise', 'myrtille', 'cassis', 'pomme', 'poire', 'pêche', 'mangue',
      'ananas', 'banane', 'pastèque', 'melon', 'raisin', 'cerise', 'mûre', 'kiwi', 'litchi',
      'passion', 'orange', 'clémentine', 'pamplemousse', 'vanille', 'caramel', 'café', 'cola', 'bonbon',
    ];

    return term.split(/(\s+)/).map((word) => {
      if (!word.trim()) return word;
      const normalizedWord = this.normalize(word);
      if (directCorrections[normalizedWord]) return directCorrections[normalizedWord];
      if (normalizedWord.length < 4) return word;

      let closest = '';
      let closestDistance = Infinity;
      vocabulary.forEach((candidate) => {
        if (candidate.includes(' ')) return;
        const distance = this.damerauLevenshtein(normalizedWord, this.normalize(candidate));
        if (distance < closestDistance) {
          closest = candidate;
          closestDistance = distance;
        }
      });
      const allowedDistance = normalizedWord.length >= 7 ? 2 : 1;
      return closestDistance <= allowedDistance ? closest : word;
    }).join('');
  }

  levenshtein(a, b) {
    const row = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
      let diagonal = row[0];
      row[0] = i;
      for (let j = 1; j <= b.length; j += 1) {
        const previous = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
        diagonal = previous;
      }
    }
    return row[b.length];
  }

  damerauLevenshtein(a, b) {
    const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
    for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;

    for (let i = 1; i <= a.length; i += 1) {
      for (let j = 1; j <= b.length; j += 1) {
        const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + substitutionCost,
        );

        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + 1);
        }
      }
    }

    return matrix[a.length][b.length];
  }

  addCorrectionNotice(markup, correctedTerm) {
    const fragment = new DOMParser().parseFromString(`<div data-cc-search-root>${markup}</div>`, 'text/html');
    const root = fragment.querySelector('[data-cc-search-root]');
    const results = root?.querySelector('#predictive-search-results');
    if (!root || !results) return markup;

    const notice = document.createElement('p');
    notice.className = 'cc-search-correction';
    notice.textContent = `Résultats proches pour « ${correctedTerm} »`;
    results.prepend(notice);
    return root.innerHTML;
  }

  render(markup, term) {
    if (this.normalize(this.input.value) !== this.normalize(term)) return;

    this.results.innerHTML = markup;
    this.open();
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close(clear = false) {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    if (clear) this.results.innerHTML = '';
  }
}

if (!customElements.get('cc-predictive-search')) {
  customElements.define('cc-predictive-search', ClassClopePredictiveSearch);
}
